import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { reduxForm, getFormValues, Field, SubmissionError } from 'redux-form'
import { formatPhoneNumber } from '@mylo/webcore/es/utility/format'

import FlowForm from './components/Flow/FlowForm'
import FlowField from './components/Flow/FlowField'
import {
  OrangeFlowButton,
  WhiteFlowButton,
  FlowButtonGrid
} from './components/Flow/FlowButton'
import Spinner from './components/Spinner'
import MaskedField from './components/MaskedField'
import Modal from './components/Modal'

class Gate extends Component {
  state = {
    ready: null,
    bypass: false
  }

  dismissPrefill = () => {
    this.setState(() => ({ ready: true }))
  }

  async componentDidMount () {
    const {
      skipPrefill,
      bypassPrefillVerification,
      gateValues,
      dispatch,
      ...ownProps
    } = this.props

    let bypass
    try {
      bypass =
        typeof bypassPrefillVerification === 'function'
          ? !!(await bypassPrefillVerification(gateValues, dispatch, ownProps))
          : false
    } catch (e) {
      console.warn('Exception caught executing bypassPrefillVerification()', e)
      bypass = false
    }

    this.props.change('bypass', bypass)

    let ready
    try {
      ready =
        typeof skipPrefill === 'function'
          ? !!(await skipPrefill(gateValues, dispatch, ownProps))
          : true
    } catch (e) {
      console.warn('Exception caught executing skipPrefill()', e)
      ready = true
    }

    this.setState(
      state => ({ ...state, ready, bypass }),
      () => {
        document.body.style.overflow = this.state.ready ? 'auto' : 'hidden'
      }
    )
  }

  async componentDidUpdate (prevProps, prevState) {
    const { ready, bypass } = this.state
    const { submitting, submitSucceeded } = this.props
    const prefillComplete = submitSucceeded && !prevProps.submitSucceeded
    const prefillAutoSubmit = ready === false && bypass && !submitting

    if (prefillComplete) {
      return this.setState(state => ({ ready: true }))
    }

    if (prefillAutoSubmit) {
      return this.props.submit()
    }

    document.body.style.overflow = ready ? 'auto' : 'hidden'
  }

  render () {
    if (this.state.ready === null) {
      return this.renderInitializing()
    }
    if (this.state.ready === false && this.props.submitting) {
      return this.renderPrefilling()
    }
    if (this.state.ready === false) {
      return this.renderCollecting()
    }
    return this.renderNothing()
  }

  renderInitializing () {
    return (
      <Modal useFlowStyling title="Just a moment" allowClose={false}>
        <Spinner />
      </Modal>
    )
  }

  renderCollecting () {
    return (
      <Modal
        useFlowStyling
        title={<span>We value your privacy ... and your time!</span>}
        allowClose={false}>
        <FlowForm onSubmit={this.props.handleSubmit}>
          <p>
            Just verify your phone number, and we'll prefill your information:
          </p>
          <Field
            id="phoneNumber"
            name="phoneNumber"
            label="Phone number"
            component={FlowField}>
            <MaskedField type="tel" phone />
          </Field>
          <FlowButtonGrid>
            <OrangeFlowButton id="thatsMeButton">That's me!</OrangeFlowButton>
            <WhiteFlowButton
              id="noThanksButton"
              type="button"
              onClick={this.dismissPrefill}>
              No thanks
            </WhiteFlowButton>
          </FlowButtonGrid>
        </FlowForm>
      </Modal>
    )
  }

  renderPrefilling () {
    return this.state.bypass ? (
      this.renderInitializing()
    ) : (
      <Modal
        useFlowStyling
        title="Verifying your information..."
        allowClose={false}>
        <Spinner />
      </Modal>
    )
  }

  renderNothing () {
    return null
  }
}

export default enhance(Gate)

export function enhance (WrappedComponent) {
  return connect(selectGateFormValues)(
    reduxForm({
      form: 'gate',
      validate: validateVerificationData,
      onSubmit: submitVerificationData
    })(WrappedComponent)
  )
}

export function selectGateFormValues (state, selector = getFormValues) {
  return {
    gateValues: selector('gate')(state)
  }
}

export function validateVerificationData ({ phoneNumber }) {
  return {
    phoneNumber:
      !phoneNumber || !/^\d{3}-\d{3}-\d{4}$/.test(phoneNumber)
        ? 'Enter a 10-digit phone number'
        : void 0
  }
}

export async function submitVerificationData (values, dispatch, ownProps) {
  try {
    if (typeof ownProps.runPrefill !== 'function') {
      throw new Error('PREFILL_NOT_FUNCTION')
    }
    await ownProps.runPrefill(
      {
        ...values,
        phoneNumber: formatPhoneNumber(values.phoneNumber, '$1$2$3')
      },
      dispatch,
      ownProps
    )
  } catch (e) {
    if (e.message === 'BAD_PHONE') {
      throw new SubmissionError({
        // eslint-disable-next-line quotes
        phoneNumber: `We couldn't find the number you provided in our system. Please try again.`
      })
    }
    if (e.message === 'TOO_MANY_REQUESTS') {
      throw new SubmissionError({
        phoneNumber:
          'You have submitted too many requests. Please click "No Thanks" to continue.'
      })
    }
    if (e.message === 'PREFILL_NOT_FUNCTION') {
      console.warn('Exception caught executing runPrefill()', e)
    } else {
      throw new SubmissionError({
        phoneNumber:
          'There was an error with your submission. Please try again.'
      })
    }
  }
}
